// Inline time entry modal launched from a Content Log row.
// Logs time against a specific content piece, with an activity type
// defaulted from the entry's current status (idea→discipline_1,
// outlined→discipline_2, drafted→editing, scheduled→posting, etc.).
//
// Hours x configured hourly rate is computed server-side. The modal
// just sends entryDate + activityType + hours + notes + contentLogId.

import { useState } from 'react';
import { apiFetch } from '../../utils/apiFetch';

const CONTENT_ACTIVITIES = [
  { id: 'discipline_1', label: 'Discipline 1 — Research / Keyword Pre-write' },
  { id: 'discipline_2', label: 'Discipline 2 — Outline (AEO format)' },
  { id: 'drafting',     label: 'Drafting — write the prose' },
  { id: 'editing',      label: 'Editing — AEO formatting + polish' },
  { id: 'posting',      label: 'Posting — WordPress upload + schema + UTM' },
  { id: 'other',        label: 'Other (content-related)' },
];

// Default activity based on the content_log entry's current status.
function defaultActivityFor(status) {
  switch (status) {
    case 'idea':      return 'discipline_1';
    case 'outlined':  return 'discipline_2';
    case 'drafted':   return 'editing';
    case 'scheduled': return 'posting';
    case 'published': return 'other';
    default:          return 'drafting';
  }
}

export default function TimeEntryModal({ contentLogId, contentTitle, contentStatus, onClose, onSaved }) {
  const [entryDate, setEntryDate] = useState(() => new Date().toISOString().slice(0, 10));
  const [activityType, setActivityType] = useState(() => defaultActivityFor(contentStatus));
  const [hours, setHours] = useState('');
  const [notes, setNotes] = useState('');
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState(null);

  async function handleSubmit(e) {
    e.preventDefault();
    setErr(null);
    setSaving(true);
    try {
      const h = Number(hours);
      if (!Number.isFinite(h) || h <= 0) throw new Error('Hours must be a positive number');
      const res = await apiFetch('/api/data/marketing/time-log', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          entryDate,
          activityType,
          hours: h,
          notes: notes || null,
          contentLogId,
        }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || `HTTP ${res.status}`);
      onSaved?.(data.entry);
      onClose();
    } catch (e) {
      setErr(e.message);
    } finally {
      setSaving(false);
    }
  }

  return (
    <div
      className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4"
      onClick={onClose}
    >
      <div
        className="bg-white rounded-[6px] shadow-lg max-w-md w-full p-5"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex justify-between items-start mb-3">
          <div>
            <div className="text-[10px] font-bold uppercase tracking-[0.12em] text-[#888]">Log Time</div>
            <div className="text-[14px] font-bold text-navy mt-0.5 truncate" title={contentTitle}>
              {contentTitle}
            </div>
          </div>
          <button
            onClick={onClose}
            className="text-[#aaa] hover:text-[#666] text-[20px] leading-none -mt-1"
          >×</button>
        </div>

        <form onSubmit={handleSubmit} className="space-y-3">
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-[10px] font-bold uppercase tracking-wider text-navy mb-1">Date</label>
              <input
                type="date"
                value={entryDate}
                onChange={e => setEntryDate(e.target.value)}
                className="w-full px-3 py-2 text-[13px] border border-gray-300 rounded-[3px] focus:outline-none focus:border-bullogic-blue"
                required
              />
            </div>
            <div>
              <label className="block text-[10px] font-bold uppercase tracking-wider text-navy mb-1">Hours</label>
              <input
                type="number"
                step="0.25"
                min="0"
                value={hours}
                onChange={e => setHours(e.target.value)}
                placeholder="2.0"
                className="w-full px-3 py-2 text-[13px] border border-gray-300 rounded-[3px] focus:outline-none focus:border-bullogic-blue"
                autoFocus
                required
              />
            </div>
          </div>

          <div>
            <label className="block text-[10px] font-bold uppercase tracking-wider text-navy mb-1">Activity</label>
            <select
              value={activityType}
              onChange={e => setActivityType(e.target.value)}
              className="w-full px-3 py-2 text-[13px] border border-gray-300 rounded-[3px] focus:outline-none focus:border-bullogic-blue"
            >
              {CONTENT_ACTIVITIES.map(a => (
                <option key={a.id} value={a.id}>{a.label}</option>
              ))}
            </select>
          </div>

          <div>
            <label className="block text-[10px] font-bold uppercase tracking-wider text-navy mb-1">Notes (optional)</label>
            <input
              value={notes}
              onChange={e => setNotes(e.target.value)}
              placeholder="What you worked on"
              className="w-full px-3 py-2 text-[13px] border border-gray-300 rounded-[3px] focus:outline-none focus:border-bullogic-blue"
            />
          </div>

          {err && <div className="text-[11px] text-rose">{err}</div>}

          <div className="flex justify-end gap-2 pt-2 border-t border-gray-100">
            <button
              type="button"
              onClick={onClose}
              className="text-[11px] font-bold px-3 py-1.5 rounded-[3px] bg-gray-100 text-[#666] hover:bg-gray-200"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={saving}
              className={`text-[11px] font-bold px-3 py-1.5 rounded-[3px] ${
                saving
                  ? 'bg-gray-200 text-gray-400 cursor-not-allowed'
                  : 'bg-bullogic-blue text-white hover:bg-bullogic-blue/90'
              }`}
            >
              {saving ? 'Logging…' : 'Log Time'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
